import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '@/components/layout/AppLayout';
import { cn } from '@/lib/utils';
import type { ApiResponse, DocRecord } from '@shared/types';
import { Loader2 } from 'lucide-react';

export function ArchivesPage() {
  const navigate = useNavigate();
  const { data: allDocs, isLoading } = useQuery({
    queryKey: ['docs'],
    queryFn: () => fetch('/api/docs').then(res => res.json() as Promise<ApiResponse<DocRecord[]>>).then(res => res.data || [])
  });

  const archives = allDocs?.filter(d => d.status === 'archived') || [];

  return (
    <AppLayout>
      <div className="flex-1 bg-surface-container-low overflow-y-auto">
        <header className="bg-surface border-b border-outline-variant px-4 md:px-margin-page py-12">
          <div className="max-w-max-width-doc mx-auto flex flex-col md:flex-row justify-between items-end gap-6">
             <div className="flex-1">
               <span className="font-label-caps text-label-caps text-tertiary uppercase tracking-widest mb-2 block">Curation</span>
               <h1 className="font-h1 text-h1 text-on-surface mb-4 italic">Historical Archives</h1>
               <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl">
                 Spanning finalized works and legacy treatises. Every document is a milestone in the forge's collective history.
               </p>
             </div>
             
             <div className="flex gap-2">
               <button className="px-6 py-3 bg-surface border border-outline-variant rounded-full font-label-caps text-label-caps uppercase tracking-widest flex items-center gap-2 hover:bg-surface-container-high transition-colors">
                 <span className="material-symbols-outlined text-[18px]">filter_list</span>
                 <span>Filter</span>
               </button>
               <div className="relative">
                 <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant text-[18px]">search</span>
                 <input 
                   type="text" 
                   placeholder="Search Archives..." 
                   className="bg-surface border border-outline-variant rounded-full py-3 pl-12 pr-6 font-label-caps text-[12px] uppercase tracking-wider text-on-surface focus:outline-none focus:ring-1 focus:ring-primary w-64"
                 />
               </div>
             </div>
          </div>
        </header>

        <main className="px-4 md:px-margin-page py-12">
          <div className="max-w-max-width-doc mx-auto flex flex-col lg:flex-row gap-12">
            {/* Sidebar Filters */}
            <aside className="w-full lg:w-48 shrink-0 space-y-8">
              <div>
                <h4 className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest mb-4">Epoch</h4>
                <div className="space-y-3">
                  {['Recent', 'Historical', 'Legacy', 'Ancient'].map(epoch => (
                    <label key={epoch} className="flex items-center gap-3 cursor-pointer group">
                      <div className="w-4 h-4 rounded border border-outline-variant group-hover:border-primary transition-colors"></div>
                      <span className="font-body-md text-body-md text-on-surface-variant group-hover:text-on-surface transition-colors">{epoch}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest mb-4">Density</h4>
                <div className="space-y-3">
                  {['High', 'Medium', 'Standard'].map(d => (
                    <label key={d} className="flex items-center gap-3 cursor-pointer group">
                      <div className="w-4 h-4 rounded-full border border-outline-variant group-hover:border-primary transition-colors"></div>
                      <span className="font-body-md text-body-md text-on-surface-variant group-hover:text-on-surface transition-colors">{d}</span>
                    </label>
                  ))}
                </div>
              </div>
            </aside>

            {/* Archives Grid */}
            <div className="flex-1">
              {isLoading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="w-10 h-10 animate-spin text-primary opacity-50" />
                </div>
              ) : archives.length === 0 ? (
                <div className="bg-surface border border-dashed border-outline-variant rounded-xl p-20 text-center flex flex-col items-center gap-4">
                   <span className="material-symbols-outlined text-[64px] text-outline">history_edu</span>
                   <p className="font-body-lg text-on-surface-variant italic">The archival vaults are currently silent. Finalize a manuscript to preserve it here.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-gutter">
                  {archives.map((doc) => (
                    <div key={doc.slug} onClick={() => navigate(`/doc/${doc.slug}`)} className="group cursor-pointer flex flex-col">
                      <div className="aspect-[4/5] bg-surface border border-outline-variant rounded-lg mb-4 paper-stack shadow-outline-variant/10 relative overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1">
                        <div className="absolute inset-0 p-8 flex flex-col justify-between opacity-40 group-hover:opacity-100 transition-opacity">
                          <div className="space-y-2">
                            <div className="w-12 h-1 bg-primary"></div>
                            <div className="w-full h-1 bg-outline-variant"></div>
                            <div className="w-3/4 h-1 bg-outline-variant"></div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-primary">verified</span>
                            <span className="font-label-caps text-[10px] uppercase tracking-widest text-primary">Certified Archive</span>
                          </div>
                        </div>
                        
                        <div className="absolute bottom-4 right-4 bg-primary text-on-primary px-3 py-1 rounded font-label-caps text-[10px] uppercase tracking-widest">
                           {doc.slug.slice(0, 3).toUpperCase()}
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-label-caps text-[10px] text-tertiary uppercase tracking-widest">Formal Manuscript</span>
                          <span className="font-label-caps text-[10px] text-on-surface-variant uppercase tracking-widest">
                            {new Date(doc.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <h3 className="font-h3 text-h3 text-on-surface text-[20px] group-hover:text-primary transition-colors leading-tight">
                          {doc.title}
                        </h3>
                        <p className="font-body-md text-body-md text-on-surface-variant italic">
                          Reference: {doc.slug}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </AppLayout>
  );
}